import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import {
  Plus, Search, RefreshCw, X, CheckSquare, Square, Users,
  TrendingUp, DollarSign, AlertCircle, Flame, List, LayoutGrid,
  ChevronDown, ArrowUpDown, UserCheck, Layers, Clock, SlidersHorizontal
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import LeadCard from '../../components/sales/LeadCard';
import LeadEditDrawer from '../../components/sales/LeadEditDrawer';
import RegisterLeadModal from '../../components/sales/RegisterLeadModal';
import EscalationModal from '../../components/EscalationModal';
import { leadsAPI, marketingAPI } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { ShieldAlert } from 'lucide-react';

const STAGES = [
  { key: 'new_lead',        label: 'New Leads',       accent: 'bg-sky-500',     dot: 'bg-sky-500',     text: 'text-sky-700' },
  { key: 'in_discussion',   label: 'In Discussion',   accent: 'bg-violet-500',  dot: 'bg-violet-500',  text: 'text-violet-700' },
  { key: 'not_interested',  label: 'Not Interested',  accent: 'bg-slate-400',   dot: 'bg-slate-400',   text: 'text-slate-600' },
  { key: 'prospective',     label: 'Prospective',     accent: 'bg-amber-500',   dot: 'bg-amber-500',   text: 'text-amber-700' },
  { key: 'payment_received',label: 'Payment Received',accent: 'bg-emerald-500', dot: 'bg-emerald-500', text: 'text-emerald-700' },
  { key: 'registered',      label: 'Registered',      accent: 'bg-teal-500',    dot: 'bg-teal-500',    text: 'text-teal-700' },
];

const SORT_OPTIONS = [
  { value: 'default', label: 'Default (Newest)' },
  { value: 'score_desc', label: 'Score: High → Low' },
  { value: 'priority', label: 'Priority: Hot first' },
  { value: 'followup', label: 'Follow-up: Soonest' },
  { value: 'value_desc', label: 'Value: High → Low' },
];

function sortLeads(leads, sortBy) {
  const arr = [...leads];
  switch (sortBy) {
    case 'score_desc': return arr.sort((a, b) => (b.lead_score || 0) - (a.lead_score || 0));
    case 'priority': {
      const p = { hot: 0, warm: 1, cold: 2 };
      return arr.sort((a, b) => (p[a.priority] ?? 1) - (p[b.priority] ?? 1));
    }
    case 'followup': return arr.sort((a, b) => {
      if (!a.follow_up_date) return 1;
      if (!b.follow_up_date) return -1;
      return new Date(a.follow_up_date) - new Date(b.follow_up_date);
    });
    case 'value_desc': return arr.sort((a, b) => (b.quoted_price || 0) - (a.quoted_price || 0));
    default: return arr.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }
}

// ─── Modals ───────────────────────────────────────────────────────────────────
function StageConfirmModal({ lead, targetStage, onConfirm, onCancel }) {
  const [reason, setReason] = useState('');
  const stage = STAGES.find((s) => s.key === targetStage);
  return (
    <Dialog open onOpenChange={onCancel}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Move to {stage?.label}?</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Moving <strong>{lead?.full_name}</strong> to <strong>{stage?.label}</strong>.</p>
        <div className="space-y-1.5">
          <Label>Reason *</Label>
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Enter reason..." />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={() => onConfirm({ reason })} disabled={!reason.trim()} className="bg-slate-700 hover:bg-slate-800">Confirm</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PaymentModal({ lead, onConfirm, onCancel }) {
  const [form, setForm] = useState({ payment_amount: lead?.quoted_price || '', payment_mode: 'cash', payment_reference: '', payment_date: new Date().toISOString().slice(0, 10) });
  const set = (f, v) => setForm((p) => ({ ...p, [f]: v }));
  return (
    <Dialog open onOpenChange={onCancel}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Record Payment — {lead?.full_name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Amount (INR)</Label><Input type="number" value={form.payment_amount} onChange={(e) => set('payment_amount', e.target.value)} /></div>
          <div className="space-y-1.5">
            <Label>Payment Mode</Label>
            <Select value={form.payment_mode} onValueChange={(v) => set('payment_mode', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{['cash', 'bank_transfer', 'card', 'cheque', 'online'].map((m) => <SelectItem key={m} value={m}>{m.replace('_', ' ')}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Reference</Label><Input value={form.payment_reference} onChange={(e) => set('payment_reference', e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={form.payment_date} onChange={(e) => set('payment_date', e.target.value)} /></div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={() => onConfirm(form)} className="bg-emerald-600 hover:bg-emerald-700">Confirm Payment</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Bulk Action Bar ──────────────────────────────────────────────────────────
function BulkActionBar({ count, onAssign, onStage, onClear }) {
  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-900 text-white rounded-2xl shadow-2xl px-5 py-3 flex items-center gap-4 min-w-[360px]">
      <div className="flex items-center gap-2">
        <CheckSquare className="w-4 h-4 text-indigo-400" />
        <span className="font-semibold">{count} selected</span>
      </div>
      <div className="w-px h-5 bg-slate-700" />
      <Button size="sm" variant="ghost" className="text-white hover:bg-slate-700 gap-1.5 h-7" onClick={onAssign}>
        <UserCheck className="w-3.5 h-3.5" /> Assign
      </Button>
      <Button size="sm" variant="ghost" className="text-white hover:bg-slate-700 gap-1.5 h-7" onClick={onStage}>
        <Layers className="w-3.5 h-3.5" /> Move Stage
      </Button>
      <Button size="sm" variant="ghost" className="text-rose-400 hover:bg-slate-700 gap-1.5 h-7 ml-auto" onClick={onClear}>
        <X className="w-3.5 h-3.5" /> Clear
      </Button>
    </div>
  );
}

function BulkAssignModal({ count, consultants, onConfirm, onCancel }) {
  const [consultantId, setConsultantId] = useState('');
  return (
    <Dialog open onOpenChange={onCancel}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Bulk Assign {count} Leads</DialogTitle></DialogHeader>
        <div className="space-y-1.5">
          <Label>Assign to Consultant</Label>
          <Select value={consultantId} onValueChange={setConsultantId}>
            <SelectTrigger><SelectValue placeholder="Select consultant..." /></SelectTrigger>
            <SelectContent>{consultants.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button disabled={!consultantId} onClick={() => onConfirm(consultantId)} className="bg-indigo-600 hover:bg-indigo-700">Assign</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkStageModal({ count, onConfirm, onCancel }) {
  const [stage, setStage] = useState('');
  const allowed = STAGES.filter((s) => !['payment_received', 'registered'].includes(s.key));
  return (
    <Dialog open onOpenChange={onCancel}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Move {count} Leads to Stage</DialogTitle></DialogHeader>
        <div className="space-y-1.5">
          <Label>Target Stage</Label>
          <Select value={stage} onValueChange={setStage}>
            <SelectTrigger><SelectValue placeholder="Select stage..." /></SelectTrigger>
            <SelectContent>{allowed.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button disabled={!stage} onClick={() => onConfirm(stage)} className="bg-slate-700 hover:bg-slate-800">Move Leads</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddLeadModal({ open, onClose, onSaved }) {
  const { user } = useAuth();
  const [consultants, setConsultants] = useState([]);
  const [sources, setSources] = useState([]);
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', whatsapp: '', nationality: '', country_of_interest: '', visa_type: '', product_interest: '', quoted_price: '', priority: 'warm', notes: '', assigned_to: '', source_label: '' });
  const [saving, setSaving] = useState(false);
  const set = (f, v) => setForm((p) => ({ ...p, [f]: v }));
  const isManager = ['sales_manager', 'super_admin', 'process_manager'].includes(user?.role);
  const canSeeSource = ['super_admin', 'process_manager', 'marketing_manager'].includes(user?.role);

  useEffect(() => {
    if (isManager) marketingAPI.getConsultants().then(({ data }) => setConsultants(data.consultants || [])).catch(() => {});
    if (canSeeSource) leadsAPI.getSources().then(({ data }) => setSources(data.sources || [])).catch(() => {});
  }, [isManager, canSeeSource]);

  const handleSave = async () => {
    if (!form.full_name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await leadsAPI.createLead({ ...form, quoted_price: form.quoted_price ? parseFloat(form.quoted_price) : null, assigned_to: form.assigned_to || (user.role === 'sales_consultant' ? user.id : null) });
      toast.success('Lead created');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Add New Lead</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="space-y-1.5"><Label>Full Name *</Label><Input value={form.full_name} onChange={(e) => set('full_name', e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={(e) => set('phone', e.target.value)} /></div>
          <div className="space-y-1.5"><Label>WhatsApp</Label><Input value={form.whatsapp} onChange={(e) => set('whatsapp', e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Nationality</Label><Input value={form.nationality} onChange={(e) => set('nationality', e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Country of Interest</Label><Input value={form.country_of_interest} onChange={(e) => set('country_of_interest', e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Visa / Product</Label><Input value={form.visa_type} onChange={(e) => set('visa_type', e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Quoted Price (INR)</Label><Input type="number" value={form.quoted_price} onChange={(e) => set('quoted_price', e.target.value)} /></div>
          <div className="space-y-1.5">
            <Label>Priority</Label>
            <Select value={form.priority} onValueChange={(v) => set('priority', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="hot">🔥 Hot</SelectItem><SelectItem value="warm">🌡 Warm</SelectItem><SelectItem value="cold">❄️ Cold</SelectItem></SelectContent>
            </Select>
          </div>
          {isManager && (
            <div className="space-y-1.5">
              <Label>Assign to</Label>
              <Select value={form.assigned_to} onValueChange={(v) => set('assigned_to', v)}>
                <SelectTrigger><SelectValue placeholder="Select consultant" /></SelectTrigger>
                <SelectContent>{consultants.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {canSeeSource && (
            <div className="space-y-1.5">
              <Label>Lead Source</Label>
              <Select value={form.source_label || 'none'} onValueChange={v => set('source_label', v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Select source" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Not specified —</SelectItem>
                  {sources.map(s => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="col-span-2 space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} /></div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">{saving ? 'Adding...' : 'Add Lead'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Kanban Column ────────────────────────────────────────────────────────────
function KanbanColumn({ stage, leads, todayLeads, onDragStart, onDragOver, onDrop, onDragLeave, isDragOver, onRefresh, bulkMode, selectedLeads, onSelectLead, onEscalate }) {
  const totalValue = leads.reduce((s, l) => s + (l.quoted_price || 0), 0);
  const hotCount = leads.filter((l) => l.priority === 'hot').length;
  const dueToday = stage.key === 'new_lead' ? todayLeads : [];

  return (
    <div
      className={`flex-shrink-0 w-72 flex flex-col rounded-xl bg-white border border-slate-200 shadow-sm transition-all duration-150 ${isDragOver ? 'ring-2 ring-teal-400 ring-offset-2 border-teal-300' : ''}`}
      onDragOver={(e) => { e.preventDefault(); onDragOver(stage.key); }}
      onDrop={(e) => { e.preventDefault(); onDrop(stage.key); }}
      onDragLeave={onDragLeave}
    >
      {/* Colored top accent bar */}
      <div className={`h-1 rounded-t-xl ${stage.accent}`} />

      <div className="px-3 pt-3 pb-2 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${stage.dot}`} />
            <span className={`font-semibold text-sm ${stage.text}`}>{stage.label}</span>
          </div>
          <div className="flex items-center gap-1.5">
            {hotCount > 0 && <Badge className="bg-rose-50 text-rose-600 border border-rose-200 text-xs px-1.5 py-0 h-5 gap-0.5"><Flame className="w-2.5 h-2.5" />{hotCount}</Badge>}
            <Badge className="bg-slate-100 text-slate-600 text-xs px-2 py-0 h-5 font-semibold">{leads.length}</Badge>
          </div>
        </div>
        {totalValue > 0 && (
          <div className="text-xs text-teal-600 font-medium mt-1.5">₹{totalValue.toLocaleString()}</div>
        )}
      </div>

      <div className={`flex-1 p-2.5 space-y-2 overflow-y-auto min-h-[200px] max-h-[calc(100vh-300px)] ${isDragOver ? 'bg-teal-50/30' : ''}`}>
        {dueToday.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 space-y-1.5 mb-1">
            <div className="flex items-center gap-1 text-xs font-semibold text-amber-700">
              <Clock className="w-3 h-3" /> Due Today ({dueToday.length})
            </div>
            {dueToday.map((lead) => (
              <div key={`today-${lead.id}`} className="pointer-events-none opacity-80">
                <LeadCard lead={lead} onRefresh={onRefresh} bulkMode={false} />
              </div>
            ))}
            <div className="border-t border-amber-200 pt-1 text-xs text-amber-600 text-center">Other leads ↓</div>
          </div>
        )}
        {leads.length === 0 && dueToday.length === 0 && (
          <div className="flex flex-col items-center justify-center h-24 text-xs text-slate-400 border-2 border-dashed border-slate-200 rounded-lg gap-1">
            <Layers className="w-4 h-4 text-slate-300" />
            Drop leads here
          </div>
        )}
        {leads.filter((l) => !dueToday.find((d) => d.id === l.id)).map((lead) => (
          <div key={lead.id} draggable={!bulkMode} onDragStart={() => !bulkMode && onDragStart(lead)} className={bulkMode ? '' : 'cursor-grab active:cursor-grabbing'}>
            <LeadCard lead={lead} onRefresh={onRefresh} bulkMode={bulkMode} isSelected={selectedLeads.has(lead.id)} onSelect={onSelectLead} />
            {onEscalate && stage.key === 'registered' && (
              <button
                onClick={(e) => { e.stopPropagation(); onEscalate(lead); }}
                className="w-full mt-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-lg border border-red-200 transition-all"
              >
                <ShieldAlert size={11} /> Escalate
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── List View Row ────────────────────────────────────────────────────────────
function ListRow({ lead, onNavigate, bulkMode, isSelected, onSelect }) {
  const navigate = useNavigate();
  const pc = { hot: 'bg-rose-400', warm: 'bg-amber-400', cold: 'bg-sky-400' };
  const score = lead.lead_score || 0;
  const days = lead.last_contacted_at ? Math.floor((Date.now() - new Date(lead.last_contacted_at)) / 86400000) : null;
  const agingCls = days === null ? 'bg-slate-300' : days <= 3 ? 'bg-emerald-400' : days <= 7 ? 'bg-amber-400' : 'bg-rose-500';
  const stage = STAGES.find((s) => s.key === lead.stage);

  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 hover:bg-slate-50 cursor-pointer border-b border-slate-100 transition-colors ${isSelected ? 'bg-indigo-50' : ''}`}
      onClick={() => bulkMode ? onSelect(lead.id) : navigate(`/sales/leads/${lead.id}`)}
    >
      {bulkMode && (
        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300'}`}>
          {isSelected && <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>}
        </div>
      )}
      <div className={`w-1 h-8 rounded-full flex-shrink-0 ${pc[lead.priority] || pc.warm}`} />
      <div className="w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
        {(lead.full_name || '?').slice(0, 2).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm text-slate-800 truncate">{lead.full_name}</p>
        <p className="text-xs text-slate-500 truncate">{lead.country_of_interest || lead.visa_type || '—'}</p>
      </div>
      <Badge className={`text-xs hidden sm:flex ${stage?.headerBg || ''} ${stage?.textColor || ''} border`}>{stage?.label}</Badge>
      {lead.quoted_price && <span className="text-xs font-semibold text-slate-700 hidden md:block">INR{Number(lead.quoted_price).toLocaleString()}</span>}
      <div className="flex items-center gap-1.5">
        <span className={`w-2 h-2 rounded-full ${agingCls}`} />
        <span className="text-xs text-slate-500 font-mono">{score}</span>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function LeadsKanban() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [todayLeads, setTodayLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [sortBy, setSortBy] = useState('default');
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' | 'list'
  const [draggedLead, setDraggedLead] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [stageConfirm, setStageConfirm] = useState(null);
  const [paymentModal, setPaymentModal] = useState(null);
  const [registerModal, setRegisterModal] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  // Escalation
  const [escalatingLead, setEscalatingLead] = useState(null);
  // Bulk state
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkStageOpen, setBulkStageOpen] = useState(false);
  const [consultants, setConsultants] = useState([]);

  const isManager = ['sales_manager', 'super_admin', 'process_manager'].includes(user?.role);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (priorityFilter) params.priority = priorityFilter;
      const { data } = await leadsAPI.getLeads(params);
      setLeads(data.leads || []);
    } catch { toast.error('Failed to load leads'); }
    finally { setLoading(false); }
  }, [search, priorityFilter]);

  const fetchTodayFollowups = useCallback(async () => {
    try {
      const { data } = await leadsAPI.getTodayFollowups();
      setTodayLeads(data.leads || []);
    } catch { /* silent */ }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      const { data } = await leadsAPI.getPipelineAnalytics();
      setAnalytics(data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchLeads(); fetchAnalytics(); fetchTodayFollowups(); }, [fetchLeads, fetchAnalytics, fetchTodayFollowups]);

  useEffect(() => {
    if (isManager) {
      marketingAPI.getConsultants().then(({ data }) => setConsultants(data.consultants || [])).catch(() => {});
    }
  }, [isManager]);

  const stageLeads = (key) => sortLeads(leads.filter((l) => l.stage === key), sortBy);

  const handleDragStart = (lead) => setDraggedLead(lead);
  const handleDragOver = (key) => setDragOverStage(key);
  const handleDragLeave = () => setDragOverStage(null);

  const handleDrop = async (targetStage) => {
    setDragOverStage(null);
    if (!draggedLead || draggedLead.stage === targetStage) { setDraggedLead(null); return; }
    if (draggedLead.stage === 'registered') { toast.error('Registered clients cannot be moved'); setDraggedLead(null); return; }
    const lead = draggedLead;
    setDraggedLead(null);
    if (targetStage === 'registered') { setRegisterModal(lead); return; }
    if (targetStage === 'payment_received') { setPaymentModal(lead); return; }
    if (targetStage === 'not_interested') { setStageConfirm({ lead, targetStage }); return; }
    await performStageChange(lead, targetStage, {});
  };

  const performStageChange = async (lead, targetStage, extra = {}) => {
    try {
      await leadsAPI.updateStage(lead.id, { stage: targetStage, ...extra });
      toast.success(`Moved to ${STAGES.find((s) => s.key === targetStage)?.label}`);
      fetchLeads(); fetchAnalytics();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to move lead'); }
  };

  // Bulk handlers
  const toggleSelectLead = (id) => {
    setSelectedLeads((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleBulkAssign = async (consultantId) => {
    setBulkAssignOpen(false);
    try {
      const { data } = await leadsAPI.bulkAssign([...selectedLeads], consultantId);
      toast.success(`${data.updated} leads reassigned`);
      setBulkMode(false); setSelectedLeads(new Set());
      fetchLeads();
    } catch (err) { toast.error(err.response?.data?.detail || 'Bulk assign failed'); }
  };

  const handleBulkStage = async (stage) => {
    setBulkStageOpen(false);
    try {
      const { data } = await leadsAPI.bulkStage([...selectedLeads], stage);
      toast.success(`${data.updated} leads moved`);
      setBulkMode(false); setSelectedLeads(new Set());
      fetchLeads();
    } catch (err) { toast.error(err.response?.data?.detail || 'Bulk stage failed'); }
  };

  const totalValue = leads.filter((l) => !['not_interested', 'registered'].includes(l.stage)).reduce((s, l) => s + (l.quoted_price || 0), 0);
  const sortedLeads = sortLeads(leads, sortBy);

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="main-content-bare flex flex-col bg-slate-50" style={{height: '100vh', overflow: 'hidden'}}>

      {/* ── Page Header ──────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-slate-200 px-6 pt-5 pb-0">
        {/* Title + actions */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Leads Pipeline</h1>
            <p className="text-sm text-slate-500 mt-0.5">Track, manage and convert your leads across stages</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchLeads} className="gap-1.5 text-slate-600">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </Button>
            <Button size="sm" onClick={() => setAddOpen(true)} className="bg-teal-600 hover:bg-teal-700 gap-1.5">
              <Plus className="w-4 h-4" /> Add Lead
            </Button>
          </div>
        </div>

        {/* KPI stat cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <div className="bg-white rounded-xl border border-slate-200 p-3.5 flex items-center gap-3">
            <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <Users className="w-4 h-4 text-teal-600" />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500">Total Leads</p>
              <p className="text-xl font-bold text-slate-900">{leads.length}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-3.5 flex items-center gap-3">
            <div className="w-8 h-8 bg-rose-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <Flame className="w-4 h-4 text-rose-500" />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500">Hot Leads</p>
              <p className="text-xl font-bold text-slate-900">{leads.filter(l => l.priority === 'hot').length}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-3.5 flex items-center gap-3">
            <div className="w-8 h-8 bg-indigo-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <DollarSign className="w-4 h-4 text-indigo-600" />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500">Pipeline Value</p>
              <p className="text-xl font-bold text-slate-900">₹{(totalValue / 1000).toFixed(0)}k</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-3.5 flex items-center gap-3">
            <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500">Conversion</p>
              <p className="text-xl font-bold text-slate-900">{analytics?.conversion_rate ?? 0}%</p>
            </div>
          </div>
        </div>

        {/* Controls row */}
        <div className="flex items-center gap-2 pb-3 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input className="pl-9 w-52" placeholder="Search leads..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <Select value={priorityFilter || 'all'} onValueChange={v => setPriorityFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-36"><SelectValue placeholder="All priorities" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All priorities</SelectItem>
              <SelectItem value="hot">🔥 Hot</SelectItem>
              <SelectItem value="warm">🌡 Warm</SelectItem>
              <SelectItem value="cold">❄️ Cold</SelectItem>
            </SelectContent>
          </Select>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5 text-slate-600">
                <ArrowUpDown className="w-3.5 h-3.5" />
                {SORT_OPTIONS.find((s) => s.value === sortBy)?.label || 'Sort'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {SORT_OPTIONS.map((s) => (
                <DropdownMenuItem key={s.value} onClick={() => setSortBy(s.value)} className={sortBy === s.value ? 'bg-teal-50 text-teal-700' : ''}>
                  {s.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {(search || priorityFilter) && (
            <Button variant="ghost" size="sm" onClick={() => { setSearch(''); setPriorityFilter(''); }} className="gap-1 text-slate-500">
              <X className="w-3.5 h-3.5" /> Clear
            </Button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <div className="flex items-center bg-slate-100 rounded-lg p-0.5">
              <Button size="sm" variant={viewMode === 'kanban' ? 'default' : 'ghost'} className={`h-7 px-2.5 ${viewMode === 'kanban' ? 'bg-white shadow-sm' : ''}`} onClick={() => setViewMode('kanban')}>
                <LayoutGrid className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" variant={viewMode === 'list' ? 'default' : 'ghost'} className={`h-7 px-2.5 ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`} onClick={() => setViewMode('list')}>
                <List className="w-3.5 h-3.5" />
              </Button>
            </div>
            <Button variant={bulkMode ? 'default' : 'outline'} size="sm"
              className={`gap-1.5 ${bulkMode ? 'bg-teal-600 hover:bg-teal-700' : 'text-slate-600'}`}
              onClick={() => { setBulkMode(!bulkMode); setSelectedLeads(new Set()); }}>
              {bulkMode ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
              Bulk
            </Button>
          </div>
        </div>

        {bulkMode && (
          <div className="mb-3 flex items-center gap-2 text-sm text-teal-700 bg-teal-50 border border-teal-200 rounded-lg px-3 py-2">
            <CheckSquare className="w-4 h-4" />
            <span>{selectedLeads.size} selected — click cards to select · use the action bar below</span>
            {selectedLeads.size > 0 && (
              <button className="ml-auto text-xs text-slate-500 hover:text-slate-700" onClick={() => setSelectedLeads(new Set())}>Clear</button>
            )}
          </div>
        )}
      </div>

      {/* ── Board ────────────────────────────────────────────────────────────── */}
      {loading ? (
        <div className="flex gap-4 p-6">
          {STAGES.map((s) => <div key={s.key} className="flex-shrink-0 w-72 h-64 bg-white rounded-xl border border-slate-200 animate-pulse" />)}
        </div>
      ) : viewMode === 'kanban' ? (
        <div className="flex-1 overflow-x-auto p-5">
          <div className="flex gap-4 h-full pb-4" style={{ minWidth: `${STAGES.length * 304}px` }}>
            {STAGES.map((stage) => (
              <KanbanColumn
                key={stage.key}
                stage={stage}
                leads={stageLeads(stage.key)}
                todayLeads={todayLeads}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onDragLeave={handleDragLeave}
                isDragOver={dragOverStage === stage.key}
                onRefresh={fetchLeads}
                bulkMode={bulkMode}
                selectedLeads={selectedLeads}
                onSelectLead={toggleSelectLead}
                onEscalate={stage.key === 'registered' ? setEscalatingLead : null}
              />
            ))}
          </div>
        </div>
      ) : (
        /* List view — mobile friendly */
        <div className="flex-1 overflow-y-auto">
          <div className="bg-white border border-slate-200 rounded-xl mx-4 my-4 overflow-hidden">
            {/* Group by stage */}
            {STAGES.map((stage) => {
              const stageL = sortedLeads.filter((l) => l.stage === stage.key);
              if (stageL.length === 0) return null;
              return (
                <div key={stage.key}>
                  <div className={`flex items-center gap-2 px-4 py-2 ${stage.headerBg} border-b`}>
                    <div className={`w-2 h-2 rounded-full ${stage.color}`} />
                    <span className={`text-xs font-semibold ${stage.textColor}`}>{stage.label}</span>
                    <Badge variant="secondary" className="text-xs px-1.5 py-0 h-4 ml-auto">{stageL.length}</Badge>
                  </div>
                  {stageL.map((lead) => (
                    <ListRow key={lead.id} lead={lead} bulkMode={bulkMode} isSelected={selectedLeads.has(lead.id)} onSelect={toggleSelectLead} />
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Bulk action bar */}
      {bulkMode && selectedLeads.size > 0 && (
        <BulkActionBar
          count={selectedLeads.size}
          onAssign={() => setBulkAssignOpen(true)}
          onStage={() => setBulkStageOpen(true)}
          onClear={() => setSelectedLeads(new Set())}
        />
      )}

      {/* Modals */}
      {addOpen && <AddLeadModal open={addOpen} onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); fetchLeads(); }} />}
      {stageConfirm && <StageConfirmModal lead={stageConfirm.lead} targetStage={stageConfirm.targetStage} onConfirm={(d) => { setStageConfirm(null); performStageChange(stageConfirm.lead, stageConfirm.targetStage, d); }} onCancel={() => setStageConfirm(null)} />}
      {paymentModal && <PaymentModal lead={paymentModal} onConfirm={(f) => { const lead = paymentModal; setPaymentModal(null); performStageChange(lead, 'payment_received', { payment_amount: parseFloat(f.payment_amount), payment_mode: f.payment_mode, payment_reference: f.payment_reference, payment_date: f.payment_date }); }} onCancel={() => setPaymentModal(null)} />}
      {escalatingLead && (
        <EscalationModal
          onClose={() => setEscalatingLead(null)}
          onSuccess={() => setEscalatingLead(null)}
          clientName={escalatingLead.full_name}
          visaType={escalatingLead.visa_type || escalatingLead.country_of_interest}
        />
      )}
      {registerModal && <RegisterLeadModal lead={registerModal} open={!!registerModal} onClose={() => setRegisterModal(null)} onConfirm={async (data) => { const lead = registerModal; setRegisterModal(null); try { const res = await leadsAPI.updateStage(lead.id, { stage: 'registered', ...data }); toast.success('Registered as client!'); if (res.data?.client_id && window.confirm('Go to client profile?')) navigate(`/admin/clients/${res.data.client_id}`); fetchLeads(); fetchAnalytics(); } catch (err) { toast.error(err.response?.data?.detail || 'Registration failed'); } }} />}
      {bulkAssignOpen && <BulkAssignModal count={selectedLeads.size} consultants={consultants} onConfirm={handleBulkAssign} onCancel={() => setBulkAssignOpen(false)} />}
      {bulkStageOpen && <BulkStageModal count={selectedLeads.size} onConfirm={handleBulkStage} onCancel={() => setBulkStageOpen(false)} />}
      </div>
    </div>
  );
}
